import { useState } from 'react';
import { ArrowLeft, ChevronRight, AlertCircle, Sparkles, Users, Leaf, CheckCircle2, PanelRightClose, PanelRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIdeas } from '@/hooks/useIdeas';
import type { Idea, GrowthStage } from '@/lib/types';
import { cn } from '@/lib/utils';

interface InstrumentLayoutProps {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}

function GrowthStageBadge({ stage }: { stage: GrowthStage }) {
  const config: Record<GrowthStage, { label: string; className: string }> = {
    seed: { label: 'Seed', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
    sprout: { label: 'Sprout', className: 'bg-lime-500/10 text-lime-600 dark:text-lime-400' },
    plant: { label: 'Plant', className: 'bg-green-500/10 text-green-600 dark:text-green-400' },
    growing: { label: 'Growing', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
    tree: { label: 'Tree', className: 'bg-primary/10 text-primary' },
  };
  const { label, className } = config[stage];
  return (
    <Badge variant="secondary" className={cn('text-xs font-normal', className)}>
      {label}
    </Badge>
  );
}

function StatusBadge({ status }: { status: Idea['status'] }) {
  if (status === 'active') {
    return (
      <Badge variant="secondary" className="text-xs font-normal bg-green-500/10 text-green-600 dark:text-green-400 gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Active
      </Badge>
    );
  }
  if (status === 'withered') {
    return (
      <Badge variant="secondary" className="text-xs font-normal bg-orange-500/10 text-orange-600 dark:text-orange-400">
        Withered
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-xs font-normal bg-muted text-muted-foreground">
      Archived
    </Badge>
  );
}

function ContextPanel({ 
  idea, 
  collapsed, 
  onToggle 
}: { 
  idea: Idea | null; 
  collapsed: boolean;
  onToggle: () => void;
}) {
  if (collapsed) {
    return (
      <div className="hidden lg:flex flex-col items-center py-4 px-2 border-l border-border bg-muted/30">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-8 w-8 text-muted-foreground"
        >
          <PanelRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="hidden lg:flex flex-col w-64 border-l border-border bg-muted/30 shrink-0">
      {/* Panel header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <span className="text-sm font-medium text-foreground">Context</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-7 w-7 text-muted-foreground"
        >
          <PanelRightClose className="h-4 w-4" />
        </Button>
      </div>

      {/* Panel content */}
      <div className="flex-1 p-4 space-y-5 overflow-y-auto">
        {idea ? (
          <>
            {/* Active Idea */}
            <div className="space-y-1.5 min-w-0">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3 shrink-0" />
                Active Idea
              </div>
              <p className="text-sm font-medium text-foreground leading-snug break-words">
                {idea.title || 'Untitled idea'}
              </p>
            </div>

            {/* Summary */}
            {idea.one_liner && (
              <div className="space-y-1.5 min-w-0">
                <div className="text-xs text-muted-foreground">Summary</div>
                <p className="text-sm text-foreground/80 leading-relaxed break-words">
                  {idea.one_liner}
                </p>
              </div>
            )}

            {/* Target Audience */}
            {idea.audience && (
              <div className="space-y-1.5 min-w-0">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Users className="h-3 w-3 shrink-0" />
                  Target Audience
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed break-words">
                  {idea.audience}
                </p>
              </div>
            )}

            {/* Status Badges */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Leaf className="h-3 w-3 shrink-0" />
                Status
              </div>
              <div className="flex flex-wrap gap-1.5">
                <GrowthStageBadge stage={idea.growth_stage} />
                <StatusBadge status={idea.status} />
              </div>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground text-center py-6">
            No idea selected
          </div>
        )}
      </div>
    </div>
  );
}

function IdeaSelector({ 
  ideas, 
  selectedId, 
  onSelect 
}: { 
  ideas: Idea[]; 
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const activeIdeas = ideas.filter(i => i.status === 'active');

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3 w-full min-w-0">
      <div className="flex items-start gap-3 min-w-0">
        <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="space-y-1 flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground break-words">Select an idea to analyze</p>
          <p className="text-xs text-muted-foreground break-words">
            Results will be broader with no idea selected.
          </p>
        </div>
      </div>
      
      <Select 
        value={selectedId || ''} 
        onValueChange={(val) => onSelect(val || null)}
      >
        <SelectTrigger className="w-full bg-background">
          <SelectValue placeholder="Choose an idea…" />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border z-50">
          {activeIdeas.length === 0 ? (
            <div className="py-4 px-3 text-sm text-muted-foreground text-center">
              No active ideas available
            </div>
          ) : (
            activeIdeas.map((idea) => (
              <SelectItem key={idea.id} value={idea.id}>
                <div className="flex items-center gap-2">
                  {idea.is_focus && <Sparkles className="h-3 w-3 text-primary shrink-0" />}
                  <span className="truncate">{idea.title || 'Untitled idea'}</span>
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

export function InstrumentLayout({ title, description, icon: Icon, children }: InstrumentLayoutProps) {
  const navigate = useNavigate();
  const { ideas, loading } = useIdeas();
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  
  // Find the focused idea or allow manual selection
  const focusedIdea = ideas.find(i => i.is_focus);
  const [manuallySelectedId, setManuallySelectedId] = useState<string | null>(null);
  
  // Use focused idea if available, otherwise use manually selected
  const activeIdea = focusedIdea || ideas.find(i => i.id === manuallySelectedId) || null;
  const showSelector = !focusedIdea && !loading;

  return (
    <div className="flex flex-1 min-h-0 w-full overflow-hidden">
      {/* Main content area */}
      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 w-full min-w-0">
          {/* Header with back navigation */}
          <div className="space-y-4 w-full min-w-0">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
              onClick={() => navigate('/app/growth-instruments')}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Growth Instruments</span>
              <span className="sm:hidden">Back</span>
            </Button>
            
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-primary shrink-0" />
                <h1 className="text-xl font-medium text-foreground break-words">{title}</h1>
              </div>
              <p className="text-sm text-muted-foreground break-words">{description}</p>
            </div>
          </div>

          {/* Idea selector (only if no focused idea) */}
          {showSelector && (
            <IdeaSelector 
              ideas={ideas} 
              selectedId={manuallySelectedId} 
              onSelect={setManuallySelectedId} 
            />
          )}

          {/* Mobile context summary */}
          {activeIdea && (
            <div className="lg:hidden rounded-lg border border-border bg-muted/30 p-3 w-full min-w-0">
              <div className="flex items-center gap-2 text-sm min-w-0">
                <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="text-muted-foreground shrink-0">Analyzing:</span>
                <span className="font-medium text-foreground truncate min-w-0">
                  {activeIdea.title || 'Untitled idea'}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
              </div>
            </div>
          )}

          {/* Main content wrapper with overflow handling */}
          <div className="w-full min-w-0 overflow-hidden">
            {children}
          </div>
        </div>
      </div>

      {/* Context panel (desktop only) */}
      <ContextPanel 
        idea={activeIdea} 
        collapsed={panelCollapsed}
        onToggle={() => setPanelCollapsed(!panelCollapsed)}
      />
    </div>
  );
}
